import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config';

function EventPage({ eventDate, onNavigateBack, onNavigateToLogin, isLoggedIn }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (eventDate) {
      fetchEvent();
    }
  }, [eventDate]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/public/event/${eventDate}/`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Event not found');
        } else if (response.status === 403) {
          throw new Error('Access denied');
        } else if (response.status === 429) {
          throw new Error('Too many requests. Please try again later.');
        }
        throw new Error('Failed to load event');
      }
      
      const data = await response.json();
      if (!data.success || !data.event) {
        throw new Error('Event not found');
      }
      setEvent(data.event);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch event:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const isUpcoming = (dateString) => {
    const eventDateObj = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDateObj.setHours(0, 0, 0, 0);
    return eventDateObj >= today;
  };

  const shareEvent = async () => {
    const eventUrl = `${window.location.origin}/event/${eventDate}`;
    const locationName = event.location?.name || 'Monk Amsterdam';
    const shareData = {
      title: `${event.day_name} at ${locationName}`,
      text: `Join us for ${event.day_name} on ${formatDate(event.date)}!`,
      url: eventUrl
    };

    // Mobile: Use native share API if available
    if (navigator.share && /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        console.log('Native share failed, falling back to clipboard');
      }
    }

    // Desktop: Copy to clipboard
    try {
      await navigator.clipboard.writeText(eventUrl);
      // Show a brief feedback
      const button = event.target || document.querySelector('[data-share-button]');
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = eventUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Link copied to clipboard!');
      } catch (copyErr) {
        alert(`Share this link: ${eventUrl}`);
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={onNavigateBack} style={styles.backButton}>
            ← Back
          </button>
          <div style={styles.logoContainer}>
            <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
            <div style={styles.logoLabel}>djedjuler</div>
          </div>
          {isLoggedIn ? (
            <div style={styles.placeholder}></div>
          ) : (
            <button onClick={onNavigateToLogin} style={styles.loginButton}>
              🔐 Login
            </button>
          )}
        </div>
        <div style={styles.loading}>Loading event...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={onNavigateBack} style={styles.backButton}>
            ← Back
          </button>
          <div style={styles.logoContainer}>
            <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
            <div style={styles.logoLabel}>djedjuler</div>
          </div>
          {isLoggedIn ? (
            <div style={styles.placeholder}></div>
          ) : (
            <button onClick={onNavigateToLogin} style={styles.loginButton}>
              🔐 Login
            </button>
          )}
        </div>
        <div style={styles.error}>
          <h2>Event Not Found</h2>
          <p>Sorry, we couldn't find an event for {eventDate}</p>
          <button onClick={onNavigateBack} style={styles.retryButton}>
            Go Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onNavigateBack} style={styles.backButton}>
          ← Back
        </button>
        <div style={styles.logoContainer}>
          <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
          <div style={styles.logoLabel}>djedjuler</div>
        </div>
        {isLoggedIn ? (
          <div style={styles.placeholder}></div>
        ) : (
          <button onClick={onNavigateToLogin} style={styles.loginButton}>
            🔐 Login
          </button>
        )}
      </div>

      {/* Event Details */}
      <div style={styles.eventContainer}>
        <div style={styles.eventCard}>
          {/* Event Header */}
          <div style={styles.eventHeader}>
            <div style={styles.eventInfo}>
              <h1 style={styles.eventName}>{event.day_name}</h1>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {event.location?.name && (
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}>
                    {event.location.name}
                  </div>
                )}
                <div style={{
                  ...styles.dateChip,
                  backgroundColor: isUpcoming(event.date) ? '#4CAF50' : '#9E9E9E'
                }}>
                  {formatDate(event.date)}
                </div>
              </div>
            </div>
          </div>

          {/* Poster Image */}
          {event.poster_url && (
            <div style={styles.posterContainer}>
              <img 
                src={event.poster_url} 
                alt={`${event.day_name} poster`}
                style={styles.poster}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Event Details */}
          <div style={styles.eventDetails}>
            <div style={styles.dateText}>
              📅 {new Date(event.date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>

            {/* DJ Lineup */}
            {event.slots && event.slots.length > 0 ? (
              <div style={styles.djSection}>
                <h2 style={styles.djHeader}>🎧 DJ Lineup</h2>
                <div style={styles.djList}>
                  {event.slots.map((slot, slotIndex) => (
                    <div key={slotIndex} style={styles.djSlot}>
                      <div style={styles.djName}>{slot.dj || 'TBD'}</div>
                      <div style={styles.djTime}>
                        {slot.start_time} - {slot.end_time}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={styles.noDjs}>
                <p>🎵 DJs coming soon...</p>
              </div>
            )}

            {/* Venue Info */}
            <div style={styles.venueSection}>
              <h3 style={styles.venueHeader}>📍 Venue</h3>
              <div style={styles.venueInfo}>
                <strong>{event.location?.name || 'Monk Amsterdam'}</strong><br />
                {event.location?.address && <>{event.location.address}<br /></>}
                {event.location?.city && <>{event.location.city}</>}
                {!event.location?.address && !event.location?.city && (
                  <>Bouldering Gym & Event Space<br />Right on the water in Amsterdam</>
                )}
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div style={styles.actionBar}>
            <button 
              style={styles.shareButton}
              onClick={shareEvent}
              data-share-button
            >
              📤 Share Event
            </button>
            <button 
              style={{
                ...styles.actionButton,
                ...(event.soundcloud_url ? styles.enabledButton : styles.disabledButton)
              }}
              onClick={() => {
                if (event.soundcloud_url) {
                  window.open(event.soundcloud_url, '_blank');
                }
              }}
              disabled={!event.soundcloud_url}
            >
              🎵 {event.soundcloud_url ? 'Listen' : 'No Recording'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    backgroundColor: '#fafafa',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  },
  header: {
    backgroundColor: 'white',
    padding: '16px 20px',
    borderBottom: '1px solid #e1e1e1',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#0095f6',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '8px 0'
  },
  logoContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    height: '32px',
    width: 'auto'
  },
  logoLabel: {
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%) rotate(-20deg)',
    backgroundColor: 'red',
    color: 'white',
    fontWeight: 'bold',
    padding: '1px 4px',
    fontSize: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap',
    borderRadius: '2px'
  },
  placeholder: {
    width: '60px'
  },
  loginButton: {
    backgroundColor: '#42a5f5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  eventContainer: {
    padding: '20px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#8e8e8e',
    fontSize: '16px'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: '#ed4956'
  },
  retryButton: {
    backgroundColor: '#0095f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    cursor: 'pointer',
    marginTop: '16px'
  },
  eventCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  eventHeader: {
    padding: '20px',
    borderBottom: '1px solid #f1f1f1'
  },
  eventInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px'
  },
  eventName: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#262626'
  },
  dateChip: {
    padding: '8px 16px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  posterContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1/1',
    overflow: 'hidden'
  },
  poster: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  eventDetails: {
    padding: '20px'
  },
  dateText: {
    fontSize: '16px',
    color: '#8e8e8e',
    marginBottom: '20px'
  },
  djSection: {
    marginBottom: '24px'
  },
  djHeader: {
    margin: '0 0 16px 0',
    fontSize: '20px',
    fontWeight: '600',
    color: '#262626'
  },
  djList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  djSlot: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f8f8f8',
    borderRadius: '12px'
  },
  djName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#262626'
  },
  djTime: {
    fontSize: '14px',
    color: '#8e8e8e',
    fontWeight: '500'
  },
  noDjs: {
    textAlign: 'center',
    padding: '24px',
    color: '#8e8e8e',
    fontStyle: 'italic',
    marginBottom: '24px'
  },
  venueSection: {
    borderTop: '1px solid #f1f1f1',
    paddingTop: '20px'
  },
  venueHeader: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#262626'
  },
  venueInfo: {
    fontSize: '14px',
    color: '#8e8e8e',
    lineHeight: '1.5'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '16px 0',
    borderTop: '1px solid #f1f1f1',
    gap: '12px'
  },
  shareButton: {
    backgroundColor: '#0095f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: '1px solid #dbdbdb',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1
  },
  enabledButton: {
    color: '#0095f6',
    borderColor: '#0095f6'
  },
  disabledButton: {
    color: '#c7c7c7',
    borderColor: '#e1e1e1',
    cursor: 'not-allowed'
  }
};

export default EventPage;